import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Plus } from 'lucide-react';

// Helper: update a nested content field
function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-500">{label}</Label>
      {children}
    </div>
  );
}

// ====== Per-Type Content Editors ======

function HeroSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-3">
      <Field label="Judul"><Input value={content.title || ''} onChange={e => set('title', e.target.value)} /></Field>
      <Field label="Sub-judul"><Input value={content.subtitle || ''} onChange={e => set('subtitle', e.target.value)} /></Field>
      <Field label="Teks Tombol"><Input value={content.button_text || ''} onChange={e => set('button_text', e.target.value)} /></Field>
      <Field label="Link Tombol"><Input value={content.button_link || ''} onChange={e => set('button_link', e.target.value)} /></Field>
      <Field label="URL Gambar Background"><Input value={content.background_image || ''} onChange={e => set('background_image', e.target.value)} placeholder="https://..." /></Field>
      <Field label="Warna Background">
        <div className="flex gap-2">
          <Input type="color" value={content.background_color || '#2563eb'} onChange={e => set('background_color', e.target.value)} className="w-12 h-9 p-1" />
          <Input value={content.background_color || '#2563eb'} onChange={e => set('background_color', e.target.value)} className="flex-1" />
        </div>
      </Field>
    </div>
  );
}

function ProductsSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-3">
      <Field label="Judul Section"><Input value={content.title || ''} onChange={e => set('title', e.target.value)} /></Field>
      <Field label="Jumlah Produk"><Input type="number" value={content.count || 8} onChange={e => set('count', parseInt(e.target.value) || 8)} min={1} max={24} /></Field>
      <Field label="Kategori (kosong = semua)"><Input value={content.category || ''} onChange={e => set('category', e.target.value)} /></Field>
      <Field label="Kolom">
        <Select value={String(content.columns || 4)} onValueChange={v => set('columns', parseInt(v))}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="2">2 Kolom</SelectItem>
            <SelectItem value="3">3 Kolom</SelectItem>
            <SelectItem value="4">4 Kolom</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

function TextSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-3">
      <Field label="Judul"><Input value={content.title || ''} onChange={e => set('title', e.target.value)} /></Field>
      <Field label="Konten (HTML)"><Textarea value={content.content || ''} onChange={e => set('content', e.target.value)} rows={6} className="font-mono text-xs" /></Field>
      <Field label="Alignment">
        <Select value={content.alignment || 'left'} onValueChange={v => set('alignment', v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="left">Kiri</SelectItem>
            <SelectItem value="center">Tengah</SelectItem>
            <SelectItem value="right">Kanan</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

function ImageSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-3">
      <Field label="URL Gambar"><Input value={content.image_url || ''} onChange={e => set('image_url', e.target.value)} placeholder="https://..." /></Field>
      <Field label="Caption"><Input value={content.caption || ''} onChange={e => set('caption', e.target.value)} /></Field>
      <Field label="Link (opsional)"><Input value={content.link || ''} onChange={e => set('link', e.target.value)} /></Field>
      <Field label="Lebar">
        <Select value={content.width || 'full'} onValueChange={v => set('width', v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="full">Full Width</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="small">Small</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

function BannerSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-3">
      <Field label="Judul"><Input value={content.title || ''} onChange={e => set('title', e.target.value)} /></Field>
      <Field label="Sub-judul"><Input value={content.subtitle || ''} onChange={e => set('subtitle', e.target.value)} /></Field>
      <Field label="Teks Tombol"><Input value={content.button_text || ''} onChange={e => set('button_text', e.target.value)} /></Field>
      <Field label="Link Tombol"><Input value={content.button_link || ''} onChange={e => set('button_link', e.target.value)} /></Field>
      <Field label="Warna Background">
        <div className="flex gap-2">
          <Input type="color" value={content.background_color || '#dc2626'} onChange={e => set('background_color', e.target.value)} className="w-12 h-9 p-1" />
          <Input value={content.background_color || '#dc2626'} onChange={e => set('background_color', e.target.value)} className="flex-1" />
        </div>
      </Field>
    </div>
  );
}

function TestimonialsSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  const items = content.items || [];
  const updateItem = (i, field, val) => {
    const newItems = [...items];
    newItems[i] = { ...newItems[i], [field]: val };
    set('items', newItems);
  };
  const removeItem = (i) => set('items', items.filter((_, idx) => idx !== i));
  const addItem = () => set('items', [...items, { name: '', text: '', rating: 5 }]);

  return (
    <div className="space-y-3">
      <Field label="Judul"><Input value={content.title || ''} onChange={e => set('title', e.target.value)} /></Field>
      <Label className="text-xs text-slate-500">Testimoni</Label>
      <div className="space-y-3 max-h-[300px] overflow-y-auto">
        {items.map((item, i) => (
          <div key={i} className="p-3 border rounded-lg bg-slate-50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500">#{i + 1}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(i)}><Trash2 size={12} className="text-red-500" /></Button>
            </div>
            <Input value={item.name || ''} onChange={e => updateItem(i, 'name', e.target.value)} placeholder="Nama" className="h-8 text-xs" />
            <Textarea value={item.text || ''} onChange={e => updateItem(i, 'text', e.target.value)} placeholder="Testimoni..." rows={2} className="text-xs" />
            <div className="flex items-center gap-2">
              <Label className="text-xs">Rating:</Label>
              <Select value={String(item.rating || 5)} onValueChange={v => updateItem(i, 'rating', parseInt(v))}>
                <SelectTrigger className="h-8 w-20"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map(r => <SelectItem key={r} value={String(r)}>{r} ⭐</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={addItem} className="w-full"><Plus size={14} className="mr-1" /> Tambah Testimoni</Button>
    </div>
  );
}

function CTASettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-3">
      <Field label="Judul"><Input value={content.title || ''} onChange={e => set('title', e.target.value)} /></Field>
      <Field label="Sub-judul"><Input value={content.subtitle || ''} onChange={e => set('subtitle', e.target.value)} /></Field>
      <Field label="Teks Tombol"><Input value={content.button_text || ''} onChange={e => set('button_text', e.target.value)} /></Field>
      <Field label="Link Tombol"><Input value={content.button_link || ''} onChange={e => set('button_link', e.target.value)} /></Field>
    </div>
  );
}

function FeaturesSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  const items = content.items || [];
  const updateItem = (i, field, val) => {
    const newItems = [...items];
    newItems[i] = { ...newItems[i], [field]: val };
    set('items', newItems);
  };
  const removeItem = (i) => set('items', items.filter((_, idx) => idx !== i));
  const addItem = () => set('items', [...items, { icon: 'shield', title: '', description: '' }]);

  return (
    <div className="space-y-3">
      <Field label="Judul"><Input value={content.title || ''} onChange={e => set('title', e.target.value)} /></Field>
      <Label className="text-xs text-slate-500">Item Fitur</Label>
      <div className="space-y-3 max-h-[300px] overflow-y-auto">
        {items.map((item, i) => (
          <div key={i} className="p-3 border rounded-lg bg-slate-50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500">#{i + 1}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(i)}><Trash2 size={12} className="text-red-500" /></Button>
            </div>
            <Field label="Icon">
              <Select value={item.icon || 'shield'} onValueChange={v => updateItem(i, 'icon', v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="truck">🚚 Truck</SelectItem>
                  <SelectItem value="shield">🛡️ Shield</SelectItem>
                  <SelectItem value="headphones">🎧 Headphones</SelectItem>
                  <SelectItem value="star">⭐ Star</SelectItem>
                  <SelectItem value="heart">❤️ Heart</SelectItem>
                  <SelectItem value="zap">⚡ Zap</SelectItem>
                  <SelectItem value="check">✅ Check</SelectItem>
                  <SelectItem value="clock">🕐 Clock</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Input value={item.title || ''} onChange={e => updateItem(i, 'title', e.target.value)} placeholder="Judul fitur" className="h-8 text-xs" />
            <Textarea value={item.description || ''} onChange={e => updateItem(i, 'description', e.target.value)} placeholder="Deskripsi..." rows={2} className="text-xs" />
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={addItem} className="w-full"><Plus size={14} className="mr-1" /> Tambah Fitur</Button>
    </div>
  );
}

function VideoSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-3">
      <Field label="Judul (opsional)"><Input value={content.title || ''} onChange={e => set('title', e.target.value)} /></Field>
      <Field label="URL Video (YouTube embed)"><Input value={content.video_url || ''} onChange={e => set('video_url', e.target.value)} placeholder="https://www.youtube.com/embed/..." /></Field>
      <Field label="Rasio Aspek">
        <Select value={content.aspect_ratio || '16:9'} onValueChange={v => set('aspect_ratio', v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="16:9">16:9</SelectItem>
            <SelectItem value="4:3">4:3</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

function SpacerSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-3">
      <Field label={`Tinggi: ${content.height || 60}px`}>
        <input type="range" min={10} max={200} value={content.height || 60} onChange={e => set('height', parseInt(e.target.value))} className="w-full" />
      </Field>
    </div>
  );
}

function DividerSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-3">
      <Field label="Gaya">
        <Select value={content.style || 'solid'} onValueChange={v => set('style', v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="solid">Solid</SelectItem>
            <SelectItem value="dashed">Dashed</SelectItem>
            <SelectItem value="dotted">Dotted</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="Warna">
        <div className="flex gap-2">
          <Input type="color" value={content.color || '#e2e8f0'} onChange={e => set('color', e.target.value)} className="w-12 h-9 p-1" />
          <Input value={content.color || '#e2e8f0'} onChange={e => set('color', e.target.value)} className="flex-1" />
        </div>
      </Field>
      <Field label={`Ketebalan: ${content.width || 1}px`}>
        <input type="range" min={1} max={8} value={content.width || 1} onChange={e => set('width', parseInt(e.target.value))} className="w-full" />
      </Field>
    </div>
  );
}

function FAQSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  const items = content.items || [];
  const updateItem = (i, field, val) => { const n = [...items]; n[i] = { ...n[i], [field]: val }; set('items', n); };
  const removeItem = (i) => set('items', items.filter((_, idx) => idx !== i));
  const addItem = () => set('items', [...items, { question: '', answer: '' }]);
  return (
    <div className="space-y-3">
      <Field label="Judul"><Input value={content.title || ''} onChange={e => set('title', e.target.value)} /></Field>
      <Label className="text-xs text-slate-500">Item FAQ</Label>
      <div className="space-y-3 max-h-[300px] overflow-y-auto">
        {items.map((item, i) => (
          <div key={i} className="p-3 border rounded-lg bg-slate-50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500">#{i + 1}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(i)}><Trash2 size={12} className="text-red-500" /></Button>
            </div>
            <Input value={item.question || ''} onChange={e => updateItem(i, 'question', e.target.value)} placeholder="Pertanyaan" className="h-8 text-xs" />
            <Textarea value={item.answer || ''} onChange={e => updateItem(i, 'answer', e.target.value)} placeholder="Jawaban..." rows={2} className="text-xs" />
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={addItem} className="w-full"><Plus size={14} className="mr-1" /> Tambah FAQ</Button>
    </div>
  );
}

function GallerySettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  const images = content.images || [];
  const updateItem = (i, field, val) => { const n = [...images]; n[i] = { ...n[i], [field]: val }; set('images', n); };
  const removeItem = (i) => set('images', images.filter((_, idx) => idx !== i));
  const addItem = () => set('images', [...images, { url: '', caption: '' }]);
  return (
    <div className="space-y-3">
      <Field label="Judul"><Input value={content.title || ''} onChange={e => set('title', e.target.value)} /></Field>
      <Field label="Kolom">
        <Select value={String(content.columns || 3)} onValueChange={v => set('columns', parseInt(v))}>
          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[2, 3, 4, 5, 6].map(c => <SelectItem key={c} value={String(c)}>{c} Kolom</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Label className="text-xs text-slate-500">Gambar</Label>
      <div className="space-y-3 max-h-[300px] overflow-y-auto">
        {images.map((img, i) => (
          <div key={i} className="p-3 border rounded-lg bg-slate-50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500">#{i + 1}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(i)}><Trash2 size={12} className="text-red-500" /></Button>
            </div>
            <Input value={img.url || ''} onChange={e => updateItem(i, 'url', e.target.value)} placeholder="URL Gambar" className="h-8 text-xs" />
            <Input value={img.caption || ''} onChange={e => updateItem(i, 'caption', e.target.value)} placeholder="Caption" className="h-8 text-xs" />
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={addItem} className="w-full"><Plus size={14} className="mr-1" /> Tambah Gambar</Button>
    </div>
  );
}

function PricingSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  const plans = content.plans || [];
  const updatePlan = (i, field, val) => { const n = [...plans]; n[i] = { ...n[i], [field]: val }; set('plans', n); };
  const removePlan = (i) => set('plans', plans.filter((_, idx) => idx !== i));
  const addPlan = () => set('plans', [...plans, { name: 'Plan', price: '0', period: '/bulan', features: ['Fitur 1'], highlighted: false }]);
  return (
    <div className="space-y-3">
      <Field label="Judul"><Input value={content.title || ''} onChange={e => set('title', e.target.value)} /></Field>
      <Label className="text-xs text-slate-500">Paket Harga</Label>
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {plans.map((plan, i) => (
          <div key={i} className="p-3 border rounded-lg bg-slate-50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500">Paket #{i + 1}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removePlan(i)}><Trash2 size={12} className="text-red-500" /></Button>
            </div>
            <Input value={plan.name || ''} onChange={e => updatePlan(i, 'name', e.target.value)} placeholder="Nama paket" className="h-8 text-xs" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={plan.price || ''} onChange={e => updatePlan(i, 'price', e.target.value)} placeholder="Harga" className="h-8 text-xs" />
              <Input value={plan.period || ''} onChange={e => updatePlan(i, 'period', e.target.value)} placeholder="/bulan" className="h-8 text-xs" />
            </div>
            <Field label="Fitur (satu per baris)">
              <Textarea value={(plan.features || []).join('\n')} onChange={e => updatePlan(i, 'features', e.target.value.split('\n'))} rows={3} className="text-xs" />
            </Field>
            <Input value={plan.button_text || ''} onChange={e => updatePlan(i, 'button_text', e.target.value)} placeholder="Teks tombol" className="h-8 text-xs" />
            <Input value={plan.button_link || ''} onChange={e => updatePlan(i, 'button_link', e.target.value)} placeholder="Link tombol" className="h-8 text-xs" />
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={plan.highlighted || false} onChange={e => updatePlan(i, 'highlighted', e.target.checked)} />
              Highlight (populer)
            </label>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={addPlan} className="w-full"><Plus size={14} className="mr-1" /> Tambah Paket</Button>
    </div>
  );
}

function CountdownSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-3">
      <Field label="Judul"><Input value={content.title || ''} onChange={e => set('title', e.target.value)} /></Field>
      <Field label="Tanggal Target"><Input type="datetime-local" value={content.target_date || ''} onChange={e => set('target_date', e.target.value)} className="h-8 text-xs" /></Field>
      <Field label="Teks Expired"><Input value={content.expired_text || ''} onChange={e => set('expired_text', e.target.value)} placeholder="Waktu telah habis!" /></Field>
    </div>
  );
}

function StatsSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  const items = content.items || [];
  const updateItem = (i, field, val) => { const n = [...items]; n[i] = { ...n[i], [field]: val }; set('items', n); };
  const removeItem = (i) => set('items', items.filter((_, idx) => idx !== i));
  const addItem = () => set('items', [...items, { value: '0', label: 'Label' }]);
  return (
    <div className="space-y-3">
      <Label className="text-xs text-slate-500">Item Statistik</Label>
      <div className="space-y-3 max-h-[300px] overflow-y-auto">
        {items.map((item, i) => (
          <div key={i} className="p-3 border rounded-lg bg-slate-50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500">#{i + 1}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(i)}><Trash2 size={12} className="text-red-500" /></Button>
            </div>
            <Input value={item.value || ''} onChange={e => updateItem(i, 'value', e.target.value)} placeholder="Angka/Nilai" className="h-8 text-xs" />
            <Input value={item.label || ''} onChange={e => updateItem(i, 'label', e.target.value)} placeholder="Label" className="h-8 text-xs" />
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={addItem} className="w-full"><Plus size={14} className="mr-1" /> Tambah Item</Button>
    </div>
  );
}

function MapSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-3">
      <Field label="Judul"><Input value={content.title || ''} onChange={e => set('title', e.target.value)} /></Field>
      <Field label="Google Maps Embed URL"><Input value={content.embed_url || ''} onChange={e => set('embed_url', e.target.value)} placeholder="https://www.google.com/maps/embed?..." /></Field>
      <Field label={`Tinggi: ${content.height || 400}px`}>
        <input type="range" min={200} max={600} value={content.height || 400} onChange={e => set('height', parseInt(e.target.value))} className="w-full" />
      </Field>
    </div>
  );
}

function SocialSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  const links = content.links || [];
  const updateItem = (i, field, val) => { const n = [...links]; n[i] = { ...n[i], [field]: val }; set('links', n); };
  const removeItem = (i) => set('links', links.filter((_, idx) => idx !== i));
  const addItem = () => set('links', [...links, { platform: 'instagram', url: '' }]);
  return (
    <div className="space-y-3">
      <Field label="Judul"><Input value={content.title || ''} onChange={e => set('title', e.target.value)} /></Field>
      <Label className="text-xs text-slate-500">Link Sosial Media</Label>
      <div className="space-y-3 max-h-[300px] overflow-y-auto">
        {links.map((link, i) => (
          <div key={i} className="p-3 border rounded-lg bg-slate-50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500">#{i + 1}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(i)}><Trash2 size={12} className="text-red-500" /></Button>
            </div>
            <Select value={link.platform || 'instagram'} onValueChange={v => updateItem(i, 'platform', v)}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="facebook">Facebook</SelectItem>
                <SelectItem value="youtube">YouTube</SelectItem>
                <SelectItem value="twitter">Twitter/X</SelectItem>
                <SelectItem value="tiktok">TikTok</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
              </SelectContent>
            </Select>
            <Input value={link.url || ''} onChange={e => updateItem(i, 'url', e.target.value)} placeholder="URL/Link" className="h-8 text-xs" />
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={addItem} className="w-full"><Plus size={14} className="mr-1" /> Tambah Link</Button>
    </div>
  );
}

function TeamSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  const members = content.members || [];
  const updateItem = (i, field, val) => { const n = [...members]; n[i] = { ...n[i], [field]: val }; set('members', n); };
  const removeItem = (i) => set('members', members.filter((_, idx) => idx !== i));
  const addItem = () => set('members', [...members, { name: '', role: '', photo_url: '', bio: '' }]);
  return (
    <div className="space-y-3">
      <Field label="Judul"><Input value={content.title || ''} onChange={e => set('title', e.target.value)} /></Field>
      <Label className="text-xs text-slate-500">Anggota Tim</Label>
      <div className="space-y-3 max-h-[300px] overflow-y-auto">
        {members.map((m, i) => (
          <div key={i} className="p-3 border rounded-lg bg-slate-50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500">#{i + 1}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(i)}><Trash2 size={12} className="text-red-500" /></Button>
            </div>
            <Input value={m.name || ''} onChange={e => updateItem(i, 'name', e.target.value)} placeholder="Nama" className="h-8 text-xs" />
            <Input value={m.role || ''} onChange={e => updateItem(i, 'role', e.target.value)} placeholder="Jabatan/Role" className="h-8 text-xs" />
            <Input value={m.photo_url || ''} onChange={e => updateItem(i, 'photo_url', e.target.value)} placeholder="URL Foto" className="h-8 text-xs" />
            <Textarea value={m.bio || ''} onChange={e => updateItem(i, 'bio', e.target.value)} placeholder="Bio singkat..." rows={2} className="text-xs" />
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={addItem} className="w-full"><Plus size={14} className="mr-1" /> Tambah Anggota</Button>
    </div>
  );
}

function LogosSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  const logos = content.logos || [];
  const updateItem = (i, field, val) => { const n = [...logos]; n[i] = { ...n[i], [field]: val }; set('logos', n); };
  const removeItem = (i) => set('logos', logos.filter((_, idx) => idx !== i));
  const addItem = () => set('logos', [...logos, { name: '', image_url: '', link: '' }]);
  return (
    <div className="space-y-3">
      <Field label="Judul"><Input value={content.title || ''} onChange={e => set('title', e.target.value)} /></Field>
      <Label className="text-xs text-slate-500">Logo/Brand</Label>
      <div className="space-y-3 max-h-[300px] overflow-y-auto">
        {logos.map((logo, i) => (
          <div key={i} className="p-3 border rounded-lg bg-slate-50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500">#{i + 1}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(i)}><Trash2 size={12} className="text-red-500" /></Button>
            </div>
            <Input value={logo.name || ''} onChange={e => updateItem(i, 'name', e.target.value)} placeholder="Nama brand" className="h-8 text-xs" />
            <Input value={logo.image_url || ''} onChange={e => updateItem(i, 'image_url', e.target.value)} placeholder="URL Logo" className="h-8 text-xs" />
            <Input value={logo.link || ''} onChange={e => updateItem(i, 'link', e.target.value)} placeholder="Link (opsional)" className="h-8 text-xs" />
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={addItem} className="w-full"><Plus size={14} className="mr-1" /> Tambah Logo</Button>
    </div>
  );
}

function NewsletterSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-3">
      <Field label="Judul"><Input value={content.title || ''} onChange={e => set('title', e.target.value)} /></Field>
      <Field label="Sub-judul"><Input value={content.subtitle || ''} onChange={e => set('subtitle', e.target.value)} /></Field>
      <Field label="Placeholder"><Input value={content.placeholder || ''} onChange={e => set('placeholder', e.target.value)} placeholder="Email Anda" /></Field>
      <Field label="Teks Tombol"><Input value={content.button_text || ''} onChange={e => set('button_text', e.target.value)} placeholder="Subscribe" /></Field>
    </div>
  );
}

// ====== Common Style Panel ======

function StyleSettings({ style = {}, onChange }) {
  const set = (k, v) => onChange({ ...style, [k]: v });
  return (
    <div className="space-y-3 pt-4 border-t border-slate-200">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Style</p>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Padding Atas">
          <Input type="number" value={parseInt(style.paddingTop) || 0} onChange={e => set('paddingTop', e.target.value ? `${e.target.value}px` : undefined)} className="h-8" />
        </Field>
        <Field label="Padding Bawah">
          <Input type="number" value={parseInt(style.paddingBottom) || 0} onChange={e => set('paddingBottom', e.target.value ? `${e.target.value}px` : undefined)} className="h-8" />
        </Field>
        <Field label="Margin Atas">
          <Input type="number" value={parseInt(style.marginTop) || 0} onChange={e => set('marginTop', e.target.value ? `${e.target.value}px` : undefined)} className="h-8" />
        </Field>
        <Field label="Margin Bawah">
          <Input type="number" value={parseInt(style.marginBottom) || 0} onChange={e => set('marginBottom', e.target.value ? `${e.target.value}px` : undefined)} className="h-8" />
        </Field>
      </div>
      <Field label="Background Color">
        <div className="flex gap-2">
          <Input type="color" value={style.backgroundColor || '#ffffff'} onChange={e => set('backgroundColor', e.target.value === '#ffffff' ? undefined : e.target.value)} className="w-12 h-9 p-1" />
          <Input value={style.backgroundColor || ''} onChange={e => set('backgroundColor', e.target.value || undefined)} placeholder="Transparan" className="flex-1" />
        </div>
      </Field>
      <Field label="Text Color">
        <div className="flex gap-2">
          <Input type="color" value={style.textColor || '#000000'} onChange={e => set('textColor', e.target.value === '#000000' ? undefined : e.target.value)} className="w-12 h-9 p-1" />
          <Input value={style.textColor || ''} onChange={e => set('textColor', e.target.value || undefined)} placeholder="Default" className="flex-1" />
        </div>
      </Field>
      <Field label="Border Radius">
        <Input type="number" value={parseInt(style.borderRadius) || 0} onChange={e => set('borderRadius', e.target.value ? `${e.target.value}px` : undefined)} className="h-8" placeholder="0" />
      </Field>
      <Field label="Max Width">
        <Select value={style.maxWidth || 'auto'} onValueChange={v => set('maxWidth', v === 'auto' ? undefined : v)}>
          <SelectTrigger className="h-8"><SelectValue placeholder="Auto" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Auto (Full)</SelectItem>
            <SelectItem value="640px">Small (640px)</SelectItem>
            <SelectItem value="768px">Medium (768px)</SelectItem>
            <SelectItem value="1024px">Large (1024px)</SelectItem>
            <SelectItem value="1280px">XL (1280px)</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="URL Gambar Background">
        <Input value={style.backgroundImage || ''} onChange={e => set('backgroundImage', e.target.value || undefined)} placeholder="https://..." />
      </Field>
    </div>
  );
}

function HtmlSettings({ content, onChange }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-3">
      <Field label="Label (opsional)"><Input value={content.label || ''} onChange={e => set('label', e.target.value)} placeholder="Tracking Pixel, Chat Widget, dll" /></Field>
      <Field label="Kode HTML / Script / CSS">
        <Textarea value={content.html_code || ''} onChange={e => set('html_code', e.target.value)} rows={12} className="font-mono text-xs" placeholder="<script>...</script> atau <style>...</style> atau HTML apapun" />
      </Field>
      <p className="text-xs text-amber-600">⚠️ Pastikan kode yang dimasukkan aman dan terpercaya</p>
    </div>
  );
}

// ====== Content Settings Map ======

const CONTENT_SETTINGS = {
  hero: HeroSettings,
  products: ProductsSettings,
  text: TextSettings,
  image: ImageSettings,
  banner: BannerSettings,
  testimonials: TestimonialsSettings,
  cta: CTASettings,
  features: FeaturesSettings,
  video: VideoSettings,
  spacer: SpacerSettings,
  divider: DividerSettings,
  faq: FAQSettings,
  gallery: GallerySettings,
  pricing: PricingSettings,
  countdown: CountdownSettings,
  stats: StatsSettings,
  map: MapSettings,
  social: SocialSettings,
  team: TeamSettings,
  logos: LogosSettings,
  newsletter: NewsletterSettings,
  html: HtmlSettings
};

// ====== Main BlockSettings Component ======

export default function BlockSettings({ block, onUpdateContent, onUpdateStyle, onToggleVisibility }) {
  if (!block) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4 py-12">
        <div className="text-4xl mb-3">👆</div>
        <p className="text-sm text-slate-500">Klik block di canvas untuk mengedit pengaturannya</p>
      </div>
    );
  }

  const ContentEditor = CONTENT_SETTINGS[block.type];

  return (
    <div className="space-y-4 pb-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-800 capitalize">{block.type}</p>
          <p className="text-xs text-slate-400">Block ID: {block.id}</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Tampilkan</Label>
          <Switch checked={block.is_visible !== false} onCheckedChange={v => onToggleVisibility(block.id, v)} />
        </div>
      </div>

      {/* Content settings */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Konten</p>
        {ContentEditor && (
          <ContentEditor content={block.content || {}} onChange={c => onUpdateContent(block.id, c)} />
        )}
      </div>

      {/* Style settings */}
      <StyleSettings style={block.style || {}} onChange={s => onUpdateStyle(block.id, s)} />
    </div>
  );
}
